/**
 * timeline-meta — icon + label + tone per element lifecycle event (pure).
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  ArrowUpCircle,
  Circle,
  Clock,
  History,
  Hourglass,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  Trash2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import type { ElementEventType } from '@trafficpulse/shared';

export interface TimelineEventMeta {
  label: string;
  Icon: LucideIcon;
  iconClass: string;
}

/** Fallback for an event string the client doesn't know (older/newer backend,
 *  hand-seeded row) — renders neutrally instead of crashing the timeline. */
const UNKNOWN_META: TimelineEventMeta = {
  label: 'Event',
  Icon: Circle,
  iconClass: 'text-muted-foreground',
};

const META: Record<ElementEventType, TimelineEventMeta> = {
  queued: { label: 'Queued', Icon: Clock, iconClass: 'text-muted-foreground' },
  promoted: {
    label: 'Promoted to live',
    Icon: ArrowUpCircle,
    iconClass: 'text-success',
  },
  'set-to-queue': {
    label: 'Set back to queue',
    Icon: Undo2,
    iconClass: 'text-primary',
  },
  'set-to-waiting': {
    label: 'Moved to waiting',
    Icon: Hourglass,
    iconClass: 'text-primary',
  },
  eliminated: {
    label: 'Eliminated',
    Icon: Trash2,
    iconClass: 'text-destructive',
  },
  restored: {
    label: 'Restored from snapshot',
    Icon: History,
    iconClass: 'text-primary',
  },
  'brought-back': {
    label: 'Brought back to queue',
    Icon: RotateCcw,
    iconClass: 'text-primary',
  },
  'cap-paused': {
    label: 'Paused for offer cap',
    Icon: PauseCircle,
    iconClass: 'text-warning',
  },
  'cap-reactivated': {
    label: 'Reactivated after offer cap',
    Icon: PlayCircle,
    iconClass: 'text-success',
  },
};

export function timelineEventMeta(event: ElementEventType): TimelineEventMeta {
  return META[event] ?? UNKNOWN_META;
}
